import { ContextMenu as ContextMenuPrimitive } from "radix-ui";

import { cn } from "@shared/lib/cn";
import { MenuPlate } from "./menu-plate";
import {
  MENU_ITEM,
  MENU_LABEL,
  MENU_SEPARATOR,
  MENU_SHORTCUT,
  POP_CONTENT,
  POP_SUB_CONTENT,
  SUB_TRIGGER,
} from "./menu-shared";

/*
 * Меню по правой кнопке.
 *
 * Отличается от `dropdown-menu.tsx` ровно тем, чем открывается: не нажатием на
 * кнопку, а правой кнопкой по опоре. Всё остальное — вид, зазоры, подсветка —
 * то же самое, и это не совпадение: во всплывающем списке система знает один
 * вид, второй заводить не за чем.
 *
 * Ради чего оно берётся, а не подделывается кнопкой нулевого размера в точке
 * щелчка (так здесь и было): Radix сам ловит `contextmenu`, сам считает точку,
 * сам откидывает меню от края экрана — и сам открывает его **долгим нажатием**
 * на касании. Последнего у подделки не было вовсе: на планшете меню канала,
 * человека в звонке и трансляции не открывалось никак.
 *
 * Компоненты Radix здесь и в соседнем файле разные, с разными типами, и
 * общего предка у них нет. Общий у них вид: наборы классов лежат одной
 * строкой на оба файла в `menu-shared.ts` — две копии одного описания уже
 * успевали разойтись.
 */
const ContextMenu = ContextMenuPrimitive.Root;
const ContextMenuTrigger = ContextMenuPrimitive.Trigger;

function ContextMenuContent({
  className,
  children,
  ...props
}: React.ComponentProps<typeof ContextMenuPrimitive.Content>) {
  return (
    <ContextMenuPrimitive.Portal>
      <ContextMenuPrimitive.Content
        data-slot="context-menu-content"
        /*
         * Стороны у этого меню нет: оно встаёт от точки щелчка, а не от края
         * опоры, поэтому `data-side` приходит всегда одним и тем же. Наезда
         * со стороны здесь нет — только появление на месте.
         */
        className={cn(POP_CONTENT, className)}
        {...props}
      >
        <MenuPlate />
        {children}
      </ContextMenuPrimitive.Content>
    </ContextMenuPrimitive.Portal>
  );
}

function ContextMenuLabel({
  className,
  ...props
}: React.ComponentProps<typeof ContextMenuPrimitive.Label>) {
  return (
    <ContextMenuPrimitive.Label
      data-slot="context-menu-label"
      className={cn(MENU_LABEL, className)}
      {...props}
    />
  );
}

function ContextMenuSeparator({
  className,
  ...props
}: React.ComponentProps<typeof ContextMenuPrimitive.Separator>) {
  return (
    <ContextMenuPrimitive.Separator
      data-slot="context-menu-separator"
      className={cn(MENU_SEPARATOR, className)}
      {...props}
    />
  );
}

function ContextMenuItem({
  className,
  variant = "default",
  ...props
}: React.ComponentProps<typeof ContextMenuPrimitive.Item> & {
  variant?: "default" | "destructive";
}) {
  return (
    <ContextMenuPrimitive.Item
      data-slot="context-menu-item"
      data-variant={variant}
      className={cn(MENU_ITEM, className)}
      {...props}
    />
  );
}

/** Правая подпись пункта: горячая клавиша, текущее значение, слово «право». */
function ContextMenuShortcut({ className, ...props }: React.ComponentProps<"span">) {
  return (
    <span
      data-slot="context-menu-shortcut"
      className={cn(MENU_SHORTCUT, className)}
      {...props}
    />
  );
}

/* Вложенное меню — то же, что у соседа: см. `dropdown-menu.tsx`. */
function ContextMenuSub(props: React.ComponentProps<typeof ContextMenuPrimitive.Sub>) {
  return <ContextMenuPrimitive.Sub data-slot="context-menu-sub" {...props} />;
}

function ContextMenuSubTrigger({
  className,
  ...props
}: React.ComponentProps<typeof ContextMenuPrimitive.SubTrigger>) {
  return (
    <ContextMenuPrimitive.SubTrigger
      data-slot="context-menu-sub-trigger"
      className={cn(SUB_TRIGGER, className)}
      {...props}
    />
  );
}

function ContextMenuSubContent({
  className,
  children,
  ...props
}: React.ComponentProps<typeof ContextMenuPrimitive.SubContent>) {
  return (
    <ContextMenuPrimitive.Portal>
      <ContextMenuPrimitive.SubContent
        data-slot="context-menu-sub-content"
        className={cn(POP_SUB_CONTENT, className)}
        {...props}
      >
        <MenuPlate />
        {children}
      </ContextMenuPrimitive.SubContent>
    </ContextMenuPrimitive.Portal>
  );
}

export {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuLabel,
  ContextMenuSeparator,
  ContextMenuShortcut,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
  ContextMenuTrigger,
};
